import {useOverlayActivity} from "components/useOverlayActivity";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {focusClaimedOutside, focusOpener, restorableOpener} from "components/sheetOpener";
import {claimSheetBand, releaseSheetBand, type SheetBandClaimant} from "./cardSheetBand";
import {
	isSheetCloseTransition, SHEET_CLOSE_FALLBACK_MS, sheetCloseMayFinish, sheetExitStep, SheetState,
} from "./cardSheetClose";
import {dismissalActsThrough, dismissesCardSheet, swallowDismissingClick} from "./cardSheetDismiss";
import {
	Body,
	BodyFade,
	Card,
	CloseButton,
	Footer,
	Head,
	Subtitle,
	Title,
	TitleAccent,
	TitleCopy,
} from "./CardSheet.styles";

/**
 * The shared NON-MODAL card sheet (ui#1054): a floating card in the band below the top pill bar, at the
 * same elevation as the bar, sliding in from the right.
 *
 * It is a plain `position: fixed` element rather than a MUI `Dialog`, and that is a behavioural choice
 * before it is a visual one. A modal marks the page behind it `aria-hidden` and covers it with a scrim,
 * which takes the top bar out of the accessibility tree AND out of reach of the mouse. Everything on
 * this sheet is something a sender does *alongside* the app — switching destination, personalising or
 * configuring a transfer — so the bar has to stay live. Hence: no portal, no scrim, no focus trap, and
 * `zIndex.panel` below `zIndex.nav`.
 *
 * Callers choose roles by meaning rather than by presentation:
 *  - the feature sheet ({@link ../FeaturePanel}) is a route-scoped surface → `role="region"`;
 *  - Personalise and Options are transient editors raised from buttons → `role="dialog"`, deliberately
 *    WITHOUT `aria-modal`, which is exactly what ARIA means by a non-modal dialog.
 *
 * The caller owns `open`. This owns the sequencing between "the caller said close" and "the sheet has
 * finished leaving": the exit transition plays first and {@link CardSheetProps.onExited} fires after it
 * (or after {@link SHEET_CLOSE_FALLBACK_MS}, in case `transitionend` never arrives), so a route change
 * or an unmount can never yank a sheet mid-slide.
 *
 * Escape closes it, yielding to any genuinely-modal dialog opened INSIDE the body — a template preview,
 * a checkout confirm — so that dialog's own Escape handling wins first. So does a click on the page
 * behind it (ui#1247), for EVERY sheet since ui#1534: the click is a dismissal and nothing more, because
 * it is swallowed, so the control it landed on does not also fire and a second click acts. The exception
 * is an anchor in the nav landmark (ui#1278), which is delivered as well as dismissing — a bar entry that
 * visibly does nothing looks exactly like a click the app never heard. That is light dismiss, not modality — there
 * is still no scrim, and {@link ./cardSheetDismiss} holds the layer rule, the act-through axis and the
 * reasoning. A sheet driven from OUTSIDE its card names that region through
 * {@link CardSheetProps.ownControlsSelector} so its own controls keep working; nothing turns dismissal
 * off any more.
 *
 * Focus moves into the sheet on open and ordinarily returns to the opener on close — outside-click
 * dismissal included, since it closes through the same handler — both through
 * `components/sheetOpener`, so a detached opener or `<body>` leaves focus where it is instead of
 * stranding the user at the top of the document. A caller that closes because another route surface has
 * claimed focus can suppress that restoration for the active close transition.
 *
 * ONE card sheet is open at a time, and the primitive enforces it: an open sheet claims the shared band
 * ({@link ./cardSheetBand}) and a later claim closes the previous holder through its own `onClose`, so
 * the destination sheet, the sender's editors and the top bar's Personalization sheet can never cover
 * each other whichever order they are raised in. An eviction deliberately skips the focus restore: the
 * surface that took the band has just focused itself, and handing focus back to the evicted sheet's
 * opener would pull the user straight back out of it.
 */
export interface CardSheetProps {
	/** Whether the sheet is open — owned by the caller. */
	open: boolean;
	/**
	 * The sheet's single close. Escape, the ✕ and a click outside the card all reach it — through
	 * {@link CardSheetProps.onDismiss} where a caller supplies one, and directly otherwise — as do a
	 * programmatic close and a band eviction. There is still no scrim: the
	 * dismissing click is caught on the document and SWALLOWED, so the page behind stays live and
	 * non-modal while the first outside click only closes — a second one acts on whatever it hits. An
	 * anchor in the nav landmark is the one target that closes the sheet AND is answered on the same
	 * click (ui#1278).
	 */
	onClose: () => void;
	/**
	 * What the sender's own DISMISSAL runs, when that has to mean more than "close" — the ✕, Escape and
	 * the outside click, and only those three. Defaults to {@link CardSheetProps.onClose}.
	 *
	 * The split exists because {@link CardSheetProps.onClose} is not only the sender's gesture: a
	 * programmatic close and a band eviction ({@link ./cardSheetBand}) arrive through it too, and those
	 * are not the sender saying "never mind". The Personalise composer is the caller that needs the
	 * difference — dismissing it DISCARDS the draft (ui#1408), so routing an eviction through the same
	 * handler would throw a half-composed personalization away because the sender opened another sheet.
	 *
	 * A dismissal still closes: this replaces nothing, and every caller that hands one over is expected
	 * to close through `onClose` itself. Sequencing, `onExited` and focus restoration are untouched,
	 * because the close still travels the one path it always did.
	 */
	onDismiss?: () => void;
	/**
	 * Whether this close returns focus to the captured opener. Defaults to true; set false only when
	 * another surface has deliberately claimed focus, such as route-driven editor dismissal.
	 */
	restoreFocusOnClose?: boolean;
	/**
	 * A CSS selector for the region OUTSIDE the card that operates this sheet, when one exists (ui#1534).
	 * A click there is the sender working this surface, so it neither dismisses nor is swallowed; a click
	 * anywhere else on the page dismisses, as it does for every sheet.
	 *
	 * Omit it for a TRANSIENT EDITOR — Personalization, Options and Personalise are raised from a button
	 * and operated entirely inside their own card, so a click elsewhere is the sender saying they are
	 * done. The one caller that needs it is the route-scoped feature sheet, whose tab strip is the top
	 * pill bar: {@link ../FeaturePanel} carries the reasoning at the call site, and
	 * `e2e/card-sheet-dismiss.spec.ts` asserts both halves — the pill still switches destination in
	 * place, and the page behind still dismisses.
	 */
	ownControlsSelector?: string;
	/** Visible sheet title, and the surface's accessible name. */
	title: string;
	/** Optional localized substring of `title` rendered in the primary accent colour. */
	titleAccent?: string;
	/**
	 * How the title is DRAWN. Defaults to `display` — the family's serif, at the shared sheet scale.
	 *
	 * `plain` is the builder's opt-in (kitecorp/transferify-ui#1953): its head sits above a two-column
	 * working surface rather than above a page of reading matter, and a 30px serif line there competes
	 * with the controls it introduces. A per-caller choice rather than a change to {@link Title}, because
	 * every other sheet in the app opens with that serif and the family's head is a shared decision.
	 */
	titleTone?: "display" | "plain";
	/** Optional supporting copy rendered directly under the title. */
	subtitle?: string;
	/**
	 * Explicit focus-return target captured by a caller before this sheet makes its opener inert. Omit to
	 * capture `document.activeElement` automatically when the sheet mounts.
	 */
	returnFocus?: HTMLElement | null;
	/**
	 * Resolves a live fallback only when the captured target was absent. Use this when the durable trigger's
	 * ref commits after the open render; resolving it at exit avoids depending on an incidental rerender.
	 */
	returnFocusFallback?: () => HTMLElement | null;
	/** Accessible label for the ✕ close button (e.g. `common.close`). */
	closeLabel: string;
	/**
	 * `region` for a route-scoped surface, `dialog` for a transient editor. Explicit rather than
	 * defaulted: it is what assistive tech announces, and the right answer differs per caller.
	 */
	role: "region" | "dialog";
	/**
	 * How wide the card is on a large screen (ui#1942). Defaults to `standard`.
	 *
	 * `wide` is for a body that is genuinely two columns — today only the personalization builder, which
	 * shows a live miniature of the recipient's page beside its controls. It is a per-caller opt-in
	 * rather than a raise of the shared width because every other sheet is one column of reading matter,
	 * which the standard card already holds: the Account sheet's cards span it (kitecorp/transferify-ui#3677)
	 * and the Billing sheet's column is capped inside it (ui#1816).
	 *
	 * Below `showcase.minWidthPx` it is exactly the standard card — see {@link ./CardSheet.styles}.
	 */
	width?: "standard" | "wide";
	/**
	 * Whether the top of the scroll region DISSOLVES rather than clipping at a hard edge (ui#1281).
	 *
	 * Defaults to true, because every sheet that scrolls has the same boundary and the same defect
	 * there: content is cut by a straight line drawn a hairline under the head's own divider, which
	 * reads as a rendering fault rather than as "there is more above". {@link ./CardSheet.styles}'s
	 * `scrollEdgeFade` carries the mechanism, and why an unscrolled sheet is untouched by it.
	 *
	 * The one caller that turns it OFF is the account personalization sheet, and the reason is at that
	 * call site: it already owns that band with an opaque sticky tab strip, so it has no hard edge to
	 * soften — and a mask would dissolve the strip itself, which is the only thing standing between the
	 * sender and the content scrolling underneath it.
	 */
	fadeScrollEdge?: boolean;
	/** Scrollable body content. */
	children: React.ReactNode;
	/** Optional sticky footer pinned below the scroll region. */
	footer?: React.ReactNode;
	/** Runs once the exit transition has finished — for a caller that must navigate or unmount after it. */
	onExited?: () => void;
	/** Remounts the body wrapper (replaying its fade) when one open sheet swaps to different content. */
	bodyKey?: React.Key;
	/** Test hook for the sheet surface. */
	testId?: string;
}

export function CardSheet({
	open, onClose, onDismiss, restoreFocusOnClose = true, ownControlsSelector, title, titleAccent,
	titleTone = "display", subtitle, returnFocus, returnFocusFallback, closeLabel, role,
	width = "standard", fadeScrollEdge = true, children, footer, onExited, bodyKey, testId,
}: CardSheetProps) {
	// The sender's own gesture, defaulted so every caller that does not care keeps the single handler it
	// always had. Never used for an eviction or a programmatic close — see the prop's contract.
	const observedExit = useOverlayActivity(open, onExited);
	const dismissHandler = onDismiss ?? onClose;
	const cardRef = useRef<HTMLElement>(null);
	const [state, setState] = useState<SheetState>("closed");
	// The element focus returns to, captured before the sheet takes it.
	const openerRef = useRef<HTMLElement | null>(null);
	// A durable trigger may commit after the opening render; resolve that fallback only when focus returns.
	const returnFocusFallbackRef = useRef(returnFocusFallback);
	// Captured once when a close begins: a later route change cannot reverse that focus decision mid-exit.
	const restoreFocusAfterExitRef = useRef(restoreFocusOnClose);
	// One-shot guard so a ✕ + Escape + fallback-timer race can only exit once per close.
	const exitedRef = useRef(false);
	// Whether this sheet is currently RAISED — set when the caller opens it, cleared when its exit
	// completes. A sheet merely rendered closed owes nobody an exit; see {@link ./cardSheetClose}.
	const raisedRef = useRef(false);
	// True while this close was forced by another sheet taking the band — see the eviction note above.
	const evictedRef = useRef(false);
	// The caller's current `onClose`, refreshed as the band is claimed below, so the claim can stay ONE
	// identity for this sheet's whole lifetime — which is what makes a re-claim a no-op — while still
	// closing through the handler the caller last rendered.
	const onCloseRef = useRef(onClose);
	const bandClaimRef = useRef<SheetBandClaimant>({
		release: () => {
			evictedRef.current = true;
			onCloseRef.current();
		},
	});

	// Enter: whenever the caller opens us, start off-screen and flip to "open" on the NEXT frame, so the
	// CSS transition has two distinct states to animate between. A caller that inerts its opener in the
	// same commit supplies the already-captured node; validation stays deferred until focus actually returns.
	useEffect(() => {
		if (!open) {
			return;
		}
		exitedRef.current = false;
		evictedRef.current = false;
		raisedRef.current = true;
		openerRef.current = returnFocus === undefined
			? restorableOpener(document.activeElement)
			: returnFocus;
		const id = requestAnimationFrame(() => setState("open"));
		return () => cancelAnimationFrame(id);
	}, [open, returnFocus]);

	// Keep live callback targets current without making any of them part of an interaction effect.
	//
	// `observedExit` is here for a sharper reason than the other two. `useOverlayActivity` hands back a
	// fresh closure on every render, and it is what `finishExit` calls — so reading it directly would
	// give `finishExit` a new identity per render, and the fallback timer below is keyed on that
	// identity. An unrelated re-render would then clear and re-arm the 250ms net, which is a safety net
	// that quietly stops being one: were `transitionend` not to arrive, a sheet re-rendering faster than
	// the fallback would never finish its exit at all.
	const observedExitRef = useRef(observedExit);
	useEffect(() => {
		returnFocusFallbackRef.current = returnFocusFallback;
		onCloseRef.current = onClose;
		observedExitRef.current = observedExit;
	});

	// The same treatment for the dismissal handler, and for a sharper reason than tidiness. A caller's
	// `onDismiss` is an inline closure by nature — the Personalise composer's reads its draft — so a
	// fresh identity arrives on EVERY render. The two dismissal listeners below are DOCUMENT listeners,
	// and re-registering them per render would reset the outside-click watcher's `pressTarget`: the
	// variable that makes "the press AND the release both landed outside" true. A poll tick landing
	// between a press and its release would silently degrade that back to release-only, which is the
	// text-selection-drag dismissal the guard exists to prevent. `dismiss` is therefore ONE identity for
	// this sheet's whole life, closing through the handler the caller last rendered.
	const dismissRef = useRef(dismissHandler);
	useEffect(() => {
		dismissRef.current = dismissHandler;
	});
	const dismiss = useCallback(() => dismissRef.current(), []);

	// Hold the band while open, and give it back the moment this sheet closes or unmounts. Claiming is
	// what closes whichever card sheet was open before — see {@link ./cardSheetBand}.
	//
	// Keyed on `open` ALONE, deliberately. Re-running this for a new `onClose` identity — an inline
	// arrow in a caller that re-renders — would release and immediately re-claim the band, and React
	// runs every cleanup in a commit before any effect: a sheet opening in that same commit would then
	// claim an EMPTY band, and the re-claim behind it would evict the sheet that was just raised.
	useEffect(() => {
		if (!open) {
			return;
		}
		const claim = bandClaimRef.current;
		claimSheetBand(claim);
		return () => releaseSheetBand(claim);
	}, [open]);

	// Keep the current preference for an outright unmount. A normal close captures it once below instead.
	useEffect(() => {
		if (open) {
			restoreFocusAfterExitRef.current = restoreFocusOnClose;
		}
	}, [open, restoreFocusOnClose]);

	const finishExit = useCallback(() => {
		if (exitedRef.current) {
			return;
		}
		exitedRef.current = true;
		raisedRef.current = false;
		setState("closed");
		// Restore only if focus is still this sheet's to give back. Outside-click dismissal (ui#1247)
		// made that conditional real: focus is the default action of `mousedown`, which fires long
		// before this exit ends, so the click that dismissed the sheet may already have put the caret in
		// a field behind it — and handing focus to the opener a fifth of a second later takes it away
		// again. Escape and the ✕ are untouched, and so is the common case of a mutation inside the
		// sheet leaving focus on `<body>`: neither is a live element outside this card.
		if (restoreFocusAfterExitRef.current && !evictedRef.current && !focusClaimedOutside(cardRef.current)) {
			focusOpener(openerRef.current ?? returnFocusFallbackRef.current?.() ?? null);
		}
		openerRef.current = null;
		observedExitRef.current();
	}, []);

	// Exit: capture this close's focus policy exactly once, then play the slide out. A route may change
	// again before the animation ends; it must not retroactively send focus back into an obscured opener.
	//
	// {@link ./cardSheetClose}'s `sheetExitStep` answers the third case too, the one that used to fall
	// through this effect and out of the render below without a word to the caller (ui#2236): a close
	// that lands before the enter frame has nothing to slide out, so its exit is already over.
	useEffect(() => {
		const step = sheetExitStep(open, state, raisedRef.current);
		if (step === "hold") {
			return;
		}
		if (step === "animate") {
			restoreFocusAfterExitRef.current = restoreFocusOnClose;
			setState("closing");
			return;
		}
		// And nothing to hand focus back: a sheet takes focus when it reaches "open", and this close beat
		// that. Honouring the caller's restore preference here would pull the sender out of whatever they
		// had just clicked, for a card they never saw settle.
		restoreFocusAfterExitRef.current = false;
		finishExit();
	}, [finishExit, open, restoreFocusOnClose, state]);

	// Fallback timer in case `transitionend` never fires (interrupted transition, dropped event,
	// reduced-motion edge) — so a close can never leave the sheet half-gone and the caller never told.
	// Gated by the same rule the real signal is: a sheet reopened mid-slide must not have its previous
	// close finished under it, or the exit it owes NEXT time never plays.
	useEffect(() => {
		if (!sheetCloseMayFinish(open, state)) {
			return;
		}
		const timer = window.setTimeout(finishExit, SHEET_CLOSE_FALLBACK_MS);
		return () => window.clearTimeout(timer);
	}, [open, state, finishExit]);

	// Focus moves into the sheet once it is open, so a keyboard user lands in the content they raised.
	useEffect(() => {
		if (state === "open") {
			cardRef.current?.focus();
		}
	}, [state]);

	// An outright unmount has no transition completion. Respect the latest open-state preference rather
	// than restoring into a surface the caller deliberately replaced, and always release the stale node.
	useEffect(() => () => {
		if (!exitedRef.current && restoreFocusAfterExitRef.current && !evictedRef.current) {
			focusOpener(openerRef.current ?? returnFocusFallbackRef.current?.() ?? null);
		}
		openerRef.current = null;
	}, []);

	const onTransitionEnd = useCallback(
		(event: React.TransitionEvent<HTMLElement>) => {
			// Ignore transitions bubbling up from descendants — only the card's own transform/opacity counts.
			if (event.target === cardRef.current
					&& isSheetCloseTransition(open, state, event.propertyName)) {
				finishExit();
			}
		},
		[open, state, finishExit],
	);

	useEffect(() => {
		if (!open) {
			return;
		}
		function onKeyDown(event: KeyboardEvent) {
			if (event.key !== "Escape") {
				return;
			}
			// Yield to a genuinely modal dialog open inside this sheet. This sheet is never that: it
			// carries no `aria-modal`, precisely so the page behind it stays live.
			if (document.querySelector('[role="dialog"][aria-modal="true"]')) {
				return;
			}
			dismiss();
		}

		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [open, dismiss]);

	// Light dismiss (ui#1247): a click on the page behind the sheet closes it and reaches nothing else —
	// the owner's rule is that such a first outside click only dismisses and a second one acts. A click
	// on CHROME instead — an anchor in the nav landmark, whether it routes or opens a dialog — is
	// delivered on the first click as well as closing the sheet (ui#1278), because a bar entry that
	// visibly does nothing is indistinguishable from a click the app never heard.
	// {@link ./cardSheetDismiss} owns both halves, the axis between them, and why each is shaped the
	// way it is.
	//
	// Wired for EVERY sheet since ui#1534 — there is no opt-out. A caller driven from outside its card
	// hands over `ownControlsSelector` instead, which takes that one region out of "the page behind"
	// without taking the rest of the page out with it.
	//
	// CAPTURE on the document, so the decision and the swallow both land before React dispatches the
	// click to whatever it hit. That ordering is what makes dismissal compose with a toggling opener
	// (ui#1243) instead of fighting it: the pill is a BUTTON, so it is still swallowed and its own
	// handler never runs, and it cannot re-open the sheet this click just closed.
	//
	// `click` rather than `pointerdown` for the same reason: the gesture the page would have acted on is
	// the click, so that is the one a swallow has to consume. It also puts the listener's installation —
	// a passive effect, after paint — safely past the propagation of the click that RAISED the sheet.
	//
	// Closing through the dismissal handler and nothing else keeps this on the exact path Escape and the
	// ✕ take, so
	// the exit transition, `onExited` and the focus restoration to `returnFocus` are unchanged, and the
	// band's eviction contract ({@link ./cardSheetBand}) is untouched.
	useEffect(() => {
		if (!open) {
			return;
		}
		// Where the gesture was PRESSED. A `click` fires on the nearest common ancestor of its press and
		// release, so a text-selection drag that starts inside the card and ends on the canvas arrives on
		// an element outside the card and would otherwise dismiss the sheet the sender was reading from.
		// Requiring BOTH ends to be outside is what makes "the sender clicked the page" mean it.
		let pressTarget: EventTarget | null = null;

		function onPointerDownCapture(event: PointerEvent) {
			pressTarget = event.target;
		}

		function onDocumentClickCapture(event: MouseEvent) {
			// Consumed per click, so a keyboard activation — which has no press at all — is judged on its
			// own target. A press that never produced a click leaves a stale value behind for exactly one
			// keyboard activation, which can only ever SUPPRESS a dismissal: the safe direction.
			const pressed = pressTarget;
			pressTarget = null;
			if (!dismissesCardSheet(cardRef.current, event.target, ownControlsSelector)) {
				return;
			}
			if (pressed !== null && !dismissesCardSheet(cardRef.current, pressed, ownControlsSelector)) {
				return;
			}
			// The sheet closes either way; only the click's onward journey differs. An anchor in the bar
			// keeps its default action AND its React handler, so whatever the sender aimed at happens on
			// this click rather than on a second one they should not have to make — a router push for the
			// four destinations, a dialog for the Resources rows and the public bar's Features/Pricing,
			// which `preventDefault` and open one instead.
			if (!dismissalActsThrough(event.target)) {
				swallowDismissingClick(event);
			}
			dismiss();
		}

		document.addEventListener("pointerdown", onPointerDownCapture, true);
		document.addEventListener("click", onDocumentClickCapture, true);
		return () => {
			document.removeEventListener("pointerdown", onPointerDownCapture, true);
			document.removeEventListener("click", onDocumentClickCapture, true);
		};
	}, [open, ownControlsSelector, dismiss]);

	// Fully closed and not opening: render nothing, so a closed sheet costs no DOM and no data fetch.
	if (!open && state === "closed") {
		return null;
	}

	// The full localized title remains the source of truth for text order and the accessible name. The
	// optional accent only identifies the exact substring to wrap; if a translation drifts, fall back to
	// the complete plain title rather than dropping or reordering any copy.
	const titleAccentIndex = titleAccent?.length ? title.indexOf(titleAccent) : -1;
	const titleContent = titleAccent && titleAccentIndex >= 0 ? (
		<>
			{title.slice(0, titleAccentIndex)}
			<TitleAccent data-title-accent>
				{title.slice(titleAccentIndex, titleAccentIndex + titleAccent.length)}
			</TitleAccent>
			{title.slice(titleAccentIndex + titleAccent.length)}
		</>
	) : title;

	return (
		<Card
			ref={cardRef}
			as={role === "dialog" ? "div" : "aside"}
			$wide={width === "wide"}
			data-state={state}
			data-testid={testId}
			role={role}
			aria-label={title}
			tabIndex={-1}
			onTransitionEnd={onTransitionEnd}
		>
			<Head $hasSubtitle={Boolean(subtitle)}>
				<TitleCopy>
					<Title $plain={titleTone === "plain"}>{titleContent}</Title>
					{subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
				</TitleCopy>
				{/* `dismiss`, like Escape and the outside click: this is the sender's own gesture, and it
				    is the one of the three a caller is most likely to mean by "dismiss". */}
				<CloseButton type="button" onClick={dismiss} aria-label={closeLabel}>✕</CloseButton>
			</Head>
			<Body $fadeScrollEdge={fadeScrollEdge}>
				<BodyFade key={bodyKey}>{children}</BodyFade>
			</Body>
			{footer != null ? <Footer>{footer}</Footer> : null}
		</Card>
	);
}
